import { FC } from 'react';
import { type CourseHearing, useGetStudentCourseQuery } from '@teachur/http-client';
import { Calendar, Clock, User, Globe } from 'lucide-react';
import { getUserTimezone, getTimezoneOffset } from '@new/features/schedule/lib/helpers';
import { timestampToDate, formatDate, formatTime } from '@new/common/lib/dateTime';
import { getLatestHearingAttempt } from '../../utils/courseHearingHelpers';

const DATE_UNAVAILABLE_LABEL = 'Date unavailable';

interface CourseHearingDrawerBodyProps {
  call: CourseHearing | null;
}

export const CourseHearingDrawerBody: FC<CourseHearingDrawerBodyProps> = ({ call }) => {
  useGetStudentCourseQuery(call?.courseId ?? 0, call?.studentId ?? 0, {
    enabled: !!call?.courseId && !!call?.studentId,
  });

  if (!call) return null;

  const attempt = getLatestHearingAttempt(call);
  const callDate = attempt ? timestampToDate(attempt.startTime) : null;
  const formattedDate = callDate ? formatDate(callDate) : DATE_UNAVAILABLE_LABEL;
  const formattedTime = callDate ? formatTime(callDate) : '';

  const userTimezone = getUserTimezone();
  const timezoneOffset = getTimezoneOffset();

  const studentName =
    `${call.student?.firstName ?? ''} ${call.student?.lastName ?? ''}`.trim() || 'Unknown student';

  const courseName = call.course?.name ?? 'Unknown course';

  return (
    <div className="flex flex-col gap-6 p-6">
      <section className="flex flex-col gap-4">
        <div className="flex items-center gap-3 text-(--neutral-black-NB700)">
          <Calendar className="h-5 w-5" />
          <div>
            <p className="m-0! [font-size:var(--fs-l5)]! font-semibold">{formattedDate}</p>
            <p className="m-0! [font-size:var(--fs-p5)]! text-(--neutral-black-NB500)!">
              {formattedTime}
            </p>
            <p className="m-0! [font-size:var(--fs-p5)]! text-(--neutral-black-NB500)! flex items-center gap-1">
              <Globe className="h-3 w-3" />
              {userTimezone} ({timezoneOffset})
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 text-(--neutral-black-NB700)">
          <User className="h-5 w-5" />
          <div>
            <p className="m-0! [font-size:var(--fs-l5)]! font-semibold">{studentName}</p>
            <p className="m-0! [font-size:var(--fs-p5)]! text-(--neutral-black-NB500)!">Student</p>
          </div>
        </div>

        <div className="flex items-center gap-3 text-(--neutral-black-NB700)">
          <Clock className="h-5 w-5" />
          <div>
            <p className="m-0! [font-size:var(--fs-l5)]! font-semibold">{courseName}</p>
            <p className="m-0! [font-size:var(--fs-p5)]! text-(--neutral-black-NB500)!">Course</p>
          </div>
        </div>
      </section>
    </div>
  );
};
